import { getFunctions, httpsCallable } from "firebase/functions";
import { getBlob, getStorage, ref, uploadBytes } from "firebase/storage";

import { firebaseApp } from "./firebase";

export const centralDocumentCategories = [
  "permits", "authorizations", "veteran_documents", "identification", "death_certificates",
  "financial", "contracts", "insurance", "service_planning", "cemetery", "crematory", "other",
] as const;

export type CentralDocumentCategory = (typeof centralDocumentCategories)[number];
export type CentralDocumentVisibility = "internal" | "family_visible";
export type CentralDocumentStatus = "awaiting_upload" | "pending_scan" | "pending_review" | "reviewed" | "rejected" | "voided";

export type CentralDocumentRecord = {
  caseId: string;
  category: CentralDocumentCategory;
  createdAt: string | null;
  currentVersion: number;
  documentId?: string;
  fileName: string;
  funeralHomeId: string;
  id: string;
  immutable: boolean;
  mimeType: string;
  notes: string;
  reviewedAt?: string | null;
  scanStatus?: "clean" | "rejected";
  sizeBytes: number;
  source: "family_upload" | "staff_upload" | "generated_form" | "signed_packet" | "system_export";
  status: CentralDocumentStatus;
  storagePath: string;
  title: string;
  updatedAt: string | null;
  uploadedAt?: string | null;
  version: number;
  visibility: CentralDocumentVisibility;
};

export type CentralDocumentRequest = {
  caseId: string;
  category: CentralDocumentCategory;
  createdAt: string | null;
  /** Present once a matching upload fulfilled the request (set server-side). */
  fulfilledAt?: string | null;
  /** Document id of the upload that fulfilled the request, when matched. */
  fulfilledDocumentId?: string | null;
  id: string;
  notes: string;
  notificationStatus: "pending" | "sent" | "failed";
  status: "pending" | "fulfilled" | "cancelled";
  title: string;
  updatedAt: string | null;
};

export type CentralDocumentUploadInput = {
  caseId: string;
  category: CentralDocumentCategory;
  fileName: string;
  mimeType: string;
  notes: string;
  sizeBytes: number;
  source?: "signed_packet";
  title: string;
  visibility: CentralDocumentVisibility;
};

const functions = getFunctions(firebaseApp, "us-east1");
const storage = getStorage(firebaseApp);
const MAX_DOWNLOAD_BYTES = 10 * 1024 * 1024 + 1;

export async function listCentralDocuments(caseId: string) {
  const callable = httpsCallable<
    { caseId: string },
    { documents: CentralDocumentRecord[]; requests: CentralDocumentRequest[] }
  >(functions, "listCentralDocuments");
  return (await callable({ caseId })).data;
}

export async function prepareCentralDocumentUpload(
  input: CentralDocumentUploadInput,
  replaceDocumentId?: string,
) {
  const callable = httpsCallable<
    CentralDocumentUploadInput & { replaceDocumentId?: string },
    { documentId: string; storagePath: string; version: number }
  >(functions, "prepareCentralDocumentUpload");
  return (await callable({ ...input, ...(replaceDocumentId ? { replaceDocumentId } : {}) })).data;
}

export async function finalizeCentralDocumentUpload(
  caseId: string,
  reservation: { documentId: string; version: number },
) {
  const callable = httpsCallable<
    { caseId: string; documentId: string; version: number },
    { document: CentralDocumentRecord }
  >(functions, "finalizeCentralDocumentUpload");
  return (await callable({ caseId, ...reservation })).data.document;
}

export async function cancelCentralDocumentUpload(
  caseId: string,
  reservation: { documentId: string; version: number },
) {
  const callable = httpsCallable<
    { caseId: string; documentId: string; version: number },
    { documentId: string; version: number }
  >(functions, "cancelCentralDocumentUpload");
  return (await callable({ caseId, ...reservation })).data;
}

export async function uploadCentralDocument(
  input: CentralDocumentUploadInput,
  file: Blob,
  replaceDocumentId?: string,
) {
  const reservation = await prepareCentralDocumentUpload(input, replaceDocumentId);
  try {
    await uploadBytes(ref(storage, reservation.storagePath), file, {
      contentType: input.mimeType,
      customMetadata: {
        caseId: input.caseId,
        category: input.category,
        documentId: reservation.documentId,
      },
    });
    return await finalizeCentralDocumentUpload(input.caseId, reservation);
  } catch (error) {
    await cancelCentralDocumentUpload(input.caseId, reservation).catch(() => undefined);
    throw error;
  }
}

export async function updateCentralDocument(
  caseId: string,
  documentId: string,
  updates: Partial<Pick<CentralDocumentRecord, "notes" | "status" | "visibility">>,
) {
  const callable = httpsCallable<
    { caseId: string; documentId: string; updates: typeof updates },
    { document: CentralDocumentRecord }
  >(functions, "updateCentralDocument");
  return (await callable({ caseId, documentId, updates })).data.document;
}

export async function scanCentralDocument(input: { caseId: string; documentId: string; result: "clean" | "rejected"; version: number }) {
  const callable = httpsCallable<typeof input, { document: CentralDocumentRecord }>(functions, "scanCentralDocument");
  return (await callable(input)).data.document;
}
export async function retryCentralDocumentScan(input: { caseId: string; documentId: string; version: number }) {
  const callable = httpsCallable<typeof input, { caseId: string; documentId: string; status: "pending_scan"; version: number }>(functions, "retryCentralDocumentScan");
  return (await callable(input)).data;
}

export async function voidCentralDocument(caseId: string, documentId: string) {
  const callable = httpsCallable<
    { caseId: string; documentId: string },
    { documentId: string }
  >(functions, "voidCentralDocument");
  return (await callable({ caseId, documentId })).data.documentId;
}

export async function createCentralDocumentRequest(input: {
  caseId: string;
  category: CentralDocumentCategory;
  notes: string;
  title: string;
}) {
  const callable = httpsCallable<typeof input, { request: CentralDocumentRequest }>(
    functions,
    "createCentralDocumentRequest",
  );
  return (await callable(input)).data.request;
}

export async function listCentralDocumentVersions(caseId: string, documentId: string) {
  const callable = httpsCallable<
    { caseId: string; documentId: string },
    { versions: CentralDocumentRecord[] }
  >(functions, "listCentralDocumentVersions");
  return (await callable({ caseId, documentId })).data.versions;
}

export async function getCentralDocumentAccessUrl(
  caseId: string,
  documentId: string,
  download: boolean,
) {
  const callable = httpsCallable<
    { caseId: string; documentId: string; download: boolean },
    { expiresInSeconds: number; url: string }
  >(functions, "getCentralDocumentAccessUrl");
  return (await callable({ caseId, documentId, download })).data.url;
}

export function getCentralDocumentBlob(storagePath: string) {
  return getBlob(ref(storage, storagePath), MAX_DOWNLOAD_BYTES);
}

export function getCentralDocumentErrorMessage(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  if (code.includes("permission-denied") || code.includes("unauthenticated") || code.includes("storage/unauthorized")) {
    return "Your Central account cannot access this document. Sign in again or contact an administrator.";
  }
  if (code.includes("invalid-argument") || code.includes("failed-precondition")) {
    return "Check the file, category, visibility, and document state, then try again.";
  }
  if (code.includes("not-found") || code.includes("object-not-found")) {
    return "The selected case, document, or stored file no longer exists. Refresh this page.";
  }
  return "Central couldn’t complete this document operation. Try again.";
}
